import { CoverageFilter } from './coverage-filter.types';
import { COVERAGE_FILTERS } from './coverage-filter.data';

interface NormalizedFilter extends CoverageFilter {
  normalizedKeys: string[];
}

export class CoverageFilterValidator {
  private normalizedFilters: NormalizedFilter[];

  /**
   * Normaliza texto removiendo acentos y convirtiendo a minúsculas
   * @param text - Texto a normalizar
   * @returns Texto normalizado sin acentos y en minúsculas
   */
  private normalize(text: string): string {
    return text
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');
  }

  /**
   * Elimina de una cadena cualquier cosa que esté entre paréntesis (incluyendo los paréntesis)
   * @param text - Texto del que eliminar el contenido entre paréntesis
   * @returns Texto sin contenido entre paréntesis
   */
  public removeParentheses(text: string): string {
    return text.replace(/\s*\([^)]*\)\s*/g, ' ').trim();
  }

  /**
   * Constructor del servicio
   * Pre-carga y normaliza todos los filtros al inicializar
   */
  constructor() {
    this.normalizedFilters = COVERAGE_FILTERS.sort((a, b) => a.order - b.order).map((filter) => ({
      ...filter,
      normalizedKeys: filter.keys.map((key) => this.normalize(key)),
    }));
  }

  /**
   * Testa una cadena contra un filtro individual
   * @param text - Texto a validar
   * @param filter - Filtro a aplicar (puede ser el original o normalizado internamente)
   * @returns true si el texto cumple con el filtro, false en caso contrario
   */
  testAgainstFilter(text: string, filter: NormalizedFilter): boolean {
    if (!filter.active) {
      return false;
    }

    const normalizedText = this.normalize(text);

    if (filter.inclusive) {
      // Todas las palabras clave deben estar presentes
      return filter.normalizedKeys.every((key) => normalizedText.includes(key));
    } else {
      // Al menos una palabra clave debe estar presente
      return filter.normalizedKeys.some((key) => normalizedText.includes(key));
    }
  }

  /**
   * Testa una cadena contra un conjunto de filtros pre-cargados
   * Retorna el número de orden si al menos un filtro coincide (OR logic)
   * @param text - Texto a validar
   * @returns el número de orden del primer filtro que coincide, o 0 si ninguno coincide
   */
  match(text: string): number {
    // Iterar sobre los filtros pre-cargados y ordenados
    for (const filter of this.normalizedFilters) {
      if (this.testAgainstFilter(text, filter)) {
        return filter.order;
      }
    }

    return 0;
  }

  /**
   * Obtiene el filtro normalizado por su número de orden
   * @param order - Número de orden del filtro
   * @returns El filtro normalizado o undefined si no existe
   */
  getNormalizedFilter(order: number): NormalizedFilter | undefined {
    return this.normalizedFilters.find((f) => f.order === order);
  }

  /**
   * Obtiene todos los filtros normalizados
   * @returns Array de filtros normalizados y ordenados
   */
  getNormalizedFilters(): NormalizedFilter[] {
    return [...this.normalizedFilters];
  }
}
